import React, { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import ArrowIcon from '@mui/icons-material/East';
import { useAuth } from '../contexts/AuthContext';
import { useApiClient } from '../utils/apiClient';
import { LoginField } from './LoginField';
import { AuthDivider } from './AuthDivider';
import { rememberReturnPath, takeReturnPathFrom } from './returnPath';
import { SsoProviderButtons, rememberSsoProvider, type SsoProvider } from './SsoProviderButtons';

export type { SsoProvider } from './SsoProviderButtons';

interface SignInFormProps {
  /** When true the password form is hidden; only the SSO buttons are shown. */
  ssoOnly?: boolean;
  providers: SsoProvider[];
  /** Info banner shown above the form, e.g. after a session expiry redirect. */
  notice?: string;
  /** Shown in the heading: "Sign in to …". */
  workspaceName?: string;
  /**
   * The Owner page (`/login/owner`): the password form alone, for when single
   * sign-on is unavailable. Linked from nowhere on the sign-in page; the
   * Require SSO setting tells Owners where it is.
   */
  ownerSignIn?: boolean;
}

function useSignInActions(_providers: SsoProvider[]) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const requestedPath = location.pathname + location.search + location.hash;
  const getNoAuth = useCallback((): string | null => null, []);
  const apiClient = useApiClient(getNoAuth);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localLoading, setLocalLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forgotVisible, setForgotVisible] = useState(false);

  const handleLocalLogin = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setLocalLoading(true);
      setError(null);
      try {
        await login(email, password);
        navigate(takeReturnPathFrom(requestedPath), { replace: true });
      } catch (err) {
        const msg = (err as Error)?.message ?? '';
        if (msg.includes('429') || /too many/i.test(msg)) {
          setError('Too many sign-in attempts. Please wait a moment and try again.');
        } else {
          setError('Check your credentials or contact your workspace administrator.');
        }
      } finally {
        setLocalLoading(false);
      }
    },
    [email, password, login, navigate, requestedPath],
  );

  const handleSsoLogin = useCallback(
    async (providerId: string) => {
      setSsoLoading(true);
      setError(null);
      try {
        sessionStorage.setItem('ssoProviderId', providerId);
        rememberSsoProvider(providerId);
        rememberReturnPath(requestedPath);
        const data = await apiClient.post<{ authUrl: string }>('/api/auth/sso/login', { providerId });
        // Validate that the backend-supplied URL is an absolute https:// URL before navigating.
        if (!data.authUrl.startsWith('https://')) {
          throw new Error('Invalid redirect URL returned from server.');
        }
        window.location.href = data.authUrl;
      } catch (err) {
        const msg = (err as Error)?.message ?? '';
        if (msg.includes('429') || /too many/i.test(msg)) {
          setError('Too many sign-in attempts. Please wait a moment and try again.');
        } else {
          setError('Network error. Please try again.');
        }
        setSsoLoading(false);
      }
    },
    [apiClient, requestedPath],
  );

  return {
    email, setEmail, password, setPassword,
    localLoading, ssoLoading, error,
    forgotVisible, setForgotVisible,
    handleLocalLogin, handleSsoLogin,
  };
}

export function SignInForm({
  ssoOnly = false,
  providers,
  notice,
  workspaceName,
  ownerSignIn = false,
}: SignInFormProps) {
  const showPasswordForm = ownerSignIn || !ssoOnly;
  const {
    email, setEmail, password, setPassword,
    localLoading, ssoLoading, error,
    forgotVisible, setForgotVisible,
    handleLocalLogin, handleSsoLogin,
  } = useSignInActions(providers);

  const forgotButton = (
    <Button
      variant="text"
      size="small"
      onClick={() => setForgotVisible(true)}
      sx={{
        fontSize: '0.71875rem',
        fontWeight: 500,
        color: 'primary.main',
        minWidth: 0,
        px: 1,
        py: 0,
        lineHeight: 1,
        '&:hover': { background: 'transparent', textDecoration: 'underline' },
      }}
    >
      Forgot?
    </Button>
  );

  return (
    <Box sx={{ maxWidth: 360, width: '100%', mx: 'auto', display: 'flex', flexDirection: 'column', gap: '22px' }}>
      {/* Header */}
      <Box>
        <Typography variant="overline" component="p" sx={{ letterSpacing: '0.12em', color: 'text.disabled', mb: 1.5 }}>
          Welcome back
        </Typography>
        <Typography component="h1" sx={{ fontSize: '1.75rem', fontWeight: 500, letterSpacing: '-0.025em' }}>
          {ownerSignIn ? 'Owner sign-in' : `Sign in to ${workspaceName || 'your workspace'}`}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          {ownerSignIn
            ? 'For Owners when single sign-on is unavailable. Every sign-in here is recorded.'
            : ssoOnly
              ? "Use your organisation's account."
              : null}
        </Typography>
      </Box>

      {/* Session-expiry notice yields to a live sign-in error once one appears.
          role="status" (polite) — it's an informational banner, not an alert. */}
      {notice && !error && (
        <Alert severity="info" role="status" sx={{ py: 0.5 }}>{notice}</Alert>
      )}
      {error && <Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert>}

      {/* SSO-only path */}
      {ssoOnly && !ownerSignIn && (
        <>
          <SsoProviderButtons
            providers={providers}
            loading={ssoLoading}
            onSelect={handleSsoLogin}
            emphasis="primary"
          />
          <Box
            sx={{
              p: '12px 14px',
              bgcolor: 'surface.faint',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              fontSize: '0.75rem',
              color: 'text.disabled',
              lineHeight: 1.55,
            }}
          >
            This workspace requires single sign-on. Password sign-in is turned off.
          </Box>
        </>
      )}

      {/* Email + password path */}
      {showPasswordForm && (
        <>
          <Box component="form" onSubmit={handleLocalLogin} noValidate>
            <Stack spacing={1.75}>
              <LoginField
                label="Work email"
                type="email"
                value={email}
                onChange={setEmail}
                autoComplete="email"
                required
                name="email"
                disabled={localLoading}
              />
              <LoginField
                label="Password"
                type="password"
                value={password}
                onChange={setPassword}
                autoComplete="current-password"
                required
                name="password"
                disabled={localLoading}
                endAdornment={forgotButton}
              />
              {forgotVisible && (
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                  Please contact your workspace administrator for password assistance.
                </Typography>
              )}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={localLoading}
                endIcon={localLoading ? undefined : <ArrowIcon />}
                sx={{ py: 1.5, mt: 0.5, fontSize: '0.875rem', fontWeight: 500 }}
              >
                {localLoading ? <CircularProgress size={20} color="inherit" /> : 'Sign in'}
              </Button>
            </Stack>
          </Box>

          {providers.length > 0 && !ownerSignIn && (
            <>
              <AuthDivider />
              <SsoProviderButtons
                providers={providers}
                loading={ssoLoading}
                onSelect={handleSsoLogin}
                emphasis="secondary"
              />
            </>
          )}
        </>
      )}

      {/* Footer */}
      <Typography variant="caption" sx={{ color: 'text.disabled', textAlign: 'center', lineHeight: 1.6 }}>
        {"Don't have an account? Contact your "}
        <Box component="span" sx={{ color: 'primary.main', fontWeight: 500 }}>
          workspace administrator
        </Box>
        .
      </Typography>
    </Box>
  );
}
